/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  HttpStatusCode,
  MerchantAccountKycStatusSimplified,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  NotificationCardBulma,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { ErrorLoadingMerchant } from "../../../../components/ErrorLoadingMerchant.js";
import { Loading } from "../../../../components/exception/loading.js";
import { useInstanceBankAccounts } from "../../../../hooks/bank.js";
import { LoginPage } from "../../../login/index.js";
import { NotFoundPageOrAdminCreate } from "../../../notfound/index.js";
import { CardTable } from "./Table.js";
import { useInstanceKYCSimplifiedBestStatusLongPolling } from "../../../../hooks/instance.js";

const TALER_SCREEN_ID = 34;

interface Props {
  onCreate: () => void;
  onSelect: (id: string) => void;
}

export function MissingBankAccountsWarning(): VNode {
  const { i18n } = useTranslationContext();
  const result = useInstanceBankAccounts();

  if (!result) return <Loading />;
  if (result instanceof TalerError) {
    return <Fragment />;
  }
  if (result.type === "fail") {
    return <Fragment />;
  }
  if (!result.body.accounts.length) {
    return (
      <NotificationCardBulma
        notification={{
          type: "WARN",
          message: i18n.str`You must provide a bank account to receive payments.`,
          description: i18n.str`Without this information, you cannot create new payment orders that are transferred to a bank account.`,
        }}
      />
    );
  }
  return <Fragment />;
}

export function LimitedKycActionWarning(): VNode {
  const { i18n } = useTranslationContext();
  const status = useInstanceKYCSimplifiedBestStatusLongPolling();

  switch (status) {
    case MerchantAccountKycStatusSimplified.ACTION_REQUIRED:
      return (
        <NotificationCardBulma
          notification={{
            type: "WARN",
            message: i18n.str`You must complete kyc requirements to receive payments.`,
            description: i18n.str`Without this information, you cannot create new payment orders that are transferred to a bank account.`,
          }}
        />
      );
    case MerchantAccountKycStatusSimplified.WARNING:
    case MerchantAccountKycStatusSimplified.ERROR:
      return (
        <NotificationCardBulma
          notification={{
            type: "WARN",
            message: i18n.str`You must must pass KYC to receive payments.`,
            description: i18n.str`Without this information, you cannot create new payment orders that are transferred to a bank account.`,
          }}
        />
      );
    case MerchantAccountKycStatusSimplified.OK:
      return <Fragment />;
    default:
      assertUnreachable(status);
  }
  return <Fragment />;
}

export default function ListOtpDevices({ onCreate, onSelect }: Props): VNode {
  const result = useInstanceBankAccounts();

  if (!result) return <Loading />;
  if (result instanceof TalerError) {
    return <ErrorLoadingMerchant error={result} />;
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.NotFound: {
        return <NotFoundPageOrAdminCreate />;
      }
      case HttpStatusCode.Unauthorized: {
        return <LoginPage focus />;
      }
      default: {
        assertUnreachable(result);
      }
    }
  }

  return (
    <Fragment>
      <MissingBankAccountsWarning />
      <section class="section is-main-section">
        <CardTable
          accounts={result.body.accounts.map((o) => ({
            ...o,
            id: String(o.h_wire),
          }))}
          onCreate={onCreate}
          onSelect={(e) => {
            onSelect(e.h_wire);
          }}
        />
      </section>
    </Fragment>
  );
}
